import { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Avatar,
  Tooltip,
  IconButton,
  Button,
  InputAdornment,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { CheckIcon } from "lucide-react";
import DragDropFileUploader from "../../../components/DragDropFileUploader";
import { commonInputStyles } from "../../../theme/CommonStyles";
import CustomIcons from "../../../assests/CustomIcons";

interface AgencySettingsProps {
  formik: any;
  styles: any;
  logoPreview: string | null;
  handleDeleteLogo: () => void;
  handleLogoChange: (files: File[]) => void;
  logoUploading: boolean;
  handleVerifySMTP: () => void;
  showPassword: boolean;
  handleTogglePassword: () => void;
  isedit: boolean;
  agency: any;
  testMail: string;
  setTestMail: (val: string) => void;
  testMailAttempted: boolean;
  setTestMailAttempted: (val: boolean) => void;
  handleTestMail: () => void;
  handleVerifyDomain: () => void;
  handleCopyCNAME: () => void;
  handleReset: () => void;
  domainError: string;
  setDomainError: (val: string) => void;
}

const AgencySettings: React.FC<AgencySettingsProps> = ({
  formik,
  styles,
  logoPreview,
  handleDeleteLogo,
  handleLogoChange,
  logoUploading,
  handleVerifySMTP,
  showPassword,
  handleTogglePassword,
  isedit,
  agency,
  testMail,
  setTestMail,
  testMailAttempted,
  setTestMailAttempted,
  handleTestMail,
  handleVerifyDomain,
  handleCopyCNAME,
  handleReset,
  domainError,
  setDomainError,
}) => {
  const isVerified =
    isedit &&
    formik.values.customDomain === agency?.customDomain &&
    agency?.domainVerified;
  const isInitial = !formik.values.customDomain || agency === null;

  const isSmtpVerified =
    isedit &&
    formik.values.smtp?.host === agency?.smtp?.host &&
    formik.values.smtp?.user === agency?.smtp?.user &&
    agency?.smtp?.verified;
  const isSmtpInitial =
    (!formik.values.smtp?.host &&
      !formik.values.smtp?.user &&
      !formik.values.smtp?.pass) ||
    agency === null;
  const [copied, setCopied] = useState(false);

  const onCopy = () => {
    handleCopyCNAME();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <form onSubmit={formik.handleSubmit} autoComplete="off">
      <Box sx={styles.sectionContainer}>
        <Typography sx={styles.sectionTitle}>Branding</Typography>
        <Typography
          sx={{
            ...styles.typography,
            color: "#6b7280",
            fontSize: "14px",
            mb: 3,
          }}
        >
          Customize your workspace name and logo
        </Typography>

        {/* Workspace Name Field */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>Workspace name</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type="text"
                name="name"
                placeholder="Enter Your Workspace name"
                sx={{ ...commonInputStyles, width: "100%" }}
                value={formik.values.name}
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Logo Upload Field */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={styles.fieldLabel}>Logo</Typography>
              <Tooltip
                title="Recommended logo dimensions"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: "#ffffff",
                      color: "#5e5d5dff",
                      border: "1px solid #d8d8d8",
                      fontSize: "12px",
                      borderRadius: "var(--small-border-radius)",
                      fontWeight: 500,
                      p: 1,
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#d8d8d8",
                    },
                  },
                }}
              >
                <span
                  style={{
                    backgroundColor: "rgb(242 242 243)",
                    borderRadius: "50%",
                    height: "20px",
                    width: "20px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <CustomIcons.AlertInfo
                    style={{
                      width: "10px",
                      height: "10px",
                    }}
                  />
                </span>
              </Tooltip>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              {logoPreview ? (
                <Box
                  sx={{
                    position: "relative",
                    width: 145,
                    height: "auto",
                    mt: 2,
                  }}
                >
                  <Avatar
                    src={logoPreview}
                    alt="Logo Preview"
                    sx={{ width: 145, height: 145 }}
                    variant="rounded"
                  />
                  <Tooltip title="Delete Logo" arrow>
                    <IconButton
                      size="small"
                      onClick={handleDeleteLogo}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        borderRadius: "50%",
                        width: 27,
                        height: 27,
                        "&:hover": {
                          background: "red",
                          color: "#fff",
                        },
                      }}
                    >
                      <DeleteForeverIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <DragDropFileUploader
                  acceptedTypes={[
                    "image/jpeg",
                    "image/png",
                    "image/jpg",
                    "image/heic",
                    "video/mp4",
                  ]}
                  maxSizeByType={{
                    "image/jpeg": 5,
                    "image/png": 5,
                    "image/jpg": 5,
                    "image/heic": 5,
                    "video/mp4": 100,
                  }}
                  showPreview={false}
                  onFilesSelected={handleLogoChange}
                  iconSize={35}
                  titleFontSize={18}
                  subTitleFontSize={14}
                  loading={logoUploading}
                />
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Privacy Policy URL Field */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>Privacy Policy URL</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type="text"
                name="privacyPolicyUrl"
                placeholder="https://myagency.com/privacy-policy"
                sx={{ ...commonInputStyles, width: "100%" }}
                value={formik.values.privacyPolicyUrl}
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Terms & Conditions URL Field */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>
              Terms & Conditions URL
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type="text"
                name="termsOfConditionUrl"
                placeholder="https://myagency.com/terms-and-conditions"
                sx={{ ...commonInputStyles, width: "100%" }}
                value={formik.values.termsOfConditionUrl}
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={styles.sectionContainer}>
        <Typography sx={styles.sectionTitle}>Book 1-1 Call</Typography>
        <Typography
          sx={{
            ...styles.typography,
            color: "#6b7280",
            fontSize: "14px",
            mb: 3,
          }}
        >
          Customize your Book 1-1 Call page with your own calendar.
        </Typography>

        {/* Setup Call Header Field */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>Page Title</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type="text"
                name="setup_call_header"
                placeholder="Enter Page Title"
                sx={{ ...commonInputStyles, width: "100%" }}
                value={formik.values.setup_call_header}
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Setup Call Description Field */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>
              Page Description
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type="text"
                name="setup_call_description"
                placeholder="Enter Page Description"
                sx={{ ...commonInputStyles, width: "100%" }}
                value={formik.values.setup_call_description}
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Setup Call Link Field */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>Calendar Link</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type="text"
                name="setup_call_link"
                placeholder="https://link.mapranking.com/widget/booking/..."
                sx={{ ...commonInputStyles, width: "100%" }}
                value={formik.values.setup_call_link}
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={styles.sectionContainer}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.sectionTitle}>
              Email Configuration
            </Typography>
            <Typography
              sx={{ ...styles.typography, color: "#6b7280", fontSize: "14px" }}
            >
              Configure SMTP settings for sending emails
            </Typography>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 12, md: 8 }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box
              sx={{
                ...styles.fieldWrapper,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Tooltip
                title={
                  agency === null
                    ? "First create workspace then verify SMTP"
                    : isSmtpVerified
                      ? "SMTP already verified"
                      : ""
                }
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: "#ffffff",
                      color: "#5e5d5dff",
                      border: "1px solid #d8d8d8",
                      fontSize: "12px",
                      borderRadius: "var(--small-border-radius)",
                      fontWeight: 500,
                      p: 1,
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#d8d8d8",
                    },
                  },
                }}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleVerifySMTP}
                    disabled={agency === null || isSmtpVerified}
                    sx={{
                      ...styles.actionButton,
                      minWidth: "fit-content",
                      whiteSpace: "nowrap",
                      backgroundColor: isSmtpInitial
                        ? "#f3f4f6"
                        : isSmtpVerified
                          ? "#e8f0e2"
                          : "#fee2e2",
                      color: isSmtpInitial
                        ? "#000000"
                        : isSmtpVerified
                          ? "#1e8e3e"
                          : "#e71212",
                      borderRadius: "16px",
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: "none",
                        backgroundColor: isSmtpInitial
                          ? "#f3f4f6"
                          : isSmtpVerified
                            ? "#e8f0e2"
                            : "#fee2e2",
                      },
                      "&:disabled": {
                        backgroundColor: isSmtpVerified ? "#E7F5DB" : "#f3f4f6",
                        color: isSmtpVerified ? "#5F9B20" : "#000000",
                      },
                    }}
                  >
                    {isSmtpVerified ? "Verified" : "Verify SMTP"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        {/* SMTP Host */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>Host</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type="text"
                name="smtp.host"
                placeholder="smtp.gmail.com"
                sx={{ ...commonInputStyles, width: "100%" }}
                value={formik.values.smtp.host}
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
        </Grid>

        {/* SMTP Port */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>Port</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type="text"
                name="smtp.port"
                placeholder="587"
                sx={{ ...commonInputStyles, width: "100%" }}
                value={
                  formik.values.smtp.port === 0 ? "" : formik.values.smtp.port
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    formik.setFieldValue(
                      "smtp.port",
                      value === "" ? 0 : Number(value),
                    );
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* SMTP Email */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>Email Address</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type="text"
                inputMode="email"
                name="smtp.user"
                placeholder="example@gmail.com"
                sx={{ ...commonInputStyles, width: "100%" }}
                value={formik.values.smtp.user}
                onChange={formik.handleChange}
                autoComplete="new-email"
                error={
                  formik.touched.smtp?.user && Boolean(formik.errors.smtp?.user)
                }
                helperText={
                  formik.touched.smtp?.user && formik.errors.smtp?.user
                }
              />
            </Box>
          </Grid>
        </Grid>

        {/* SMTP Password */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>Password</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <TextField
                type={showPassword ? "text" : "password"}
                name="smtp.pass"
                placeholder="Enter Password"
                sx={{
                  ...commonInputStyles,
                  width: "100%",
                }}
                value={formik.values.smtp.pass}
                onChange={(e) => {
                  const value = e.target.value.replace(/\s/g, "");
                  formik.setFieldValue("smtp.pass", value);
                }}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        disableRipple
                        disableFocusRipple
                        onClick={handleTogglePassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Test Email (Only for active agencies) */}
        {agency?.status === "active" && (
          <Grid sx={{ mt: 2 }} container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <Typography sx={styles.fieldLabel}>Test Email</Typography>
              <Typography
                sx={{
                  ...styles.typography,
                  color: "#6b7280",
                  fontSize: "14px",
                }}
              >
                Send a test email to confirm email setup
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 8 }}>
              <Box
                sx={{
                  ...styles.fieldWrapper,
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <TextField
                  type="email"
                  name="testMail"
                  placeholder="Enter email to test SMTP"
                  sx={{ ...commonInputStyles, flex: 1 }}
                  value={testMail}
                  onChange={(e) => {
                    setTestMail(e.target.value);
                    if (testMailAttempted) setTestMailAttempted(false);
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleTestMail}
                  sx={{
                    ...styles.actionButton,
                    minWidth: "fit-content",
                    whiteSpace: "nowrap",
                    border: "1px solid #d1d5db",
                    color: "#000000",
                  }}
                >
                  Test email
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* ========================================================
            SECTION 3: CUSTOM DOMAIN
        ======================================================== */}
      <Box sx={styles.sectionContainer}>
        <Typography sx={styles.sectionTitle}>Custom Domain</Typography>
        <Typography
          sx={{
            ...styles.typography,
            color: "#6b7280",
            fontSize: "14px",
            mb: 3,
          }}
        >
          Configure your custom domain for white-label branding
        </Typography>

        {/* Domain Input */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>White Label Domain</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box
              sx={{
                ...styles.fieldWrapper,
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <TextField
                type="text"
                name="customDomain"
                placeholder="example.mydomain.com"
                sx={{ ...commonInputStyles, flex: 1 }}
                value={formik.values.customDomain}
                onChange={(e) => {
                  formik.handleChange(e);
                  if (domainError) setDomainError("");
                }}
                error={
                  Boolean(domainError) ||
                  (formik.touched.customDomain &&
                    Boolean(formik.errors.customDomain))
                }
                helperText={
                  domainError ||
                  (formik.touched.customDomain && formik.errors.customDomain)
                }
              />

              <Tooltip
                title={
                  agency === null
                    ? "First create workspace then verify domain"
                    : isedit &&
                        formik.values.customDomain === agency?.customDomain &&
                        agency?.domainVerified
                      ? "Domain already verified"
                      : ""
                }
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: "#ffffff",
                      color: "#5e5d5dff",
                      border: "1px solid #d8d8d8",
                      fontSize: "12px",
                      borderRadius: "var(--small-border-radius)",
                      fontWeight: 500,
                      p: 1,
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#d8d8d8",
                    },
                  },
                }}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleVerifyDomain}
                    disabled={agency === null || isVerified}
                    sx={{
                      ...styles.actionButton,
                      minWidth: "fit-content",
                      whiteSpace: "nowrap",
                      backgroundColor: isInitial
                        ? "#f3f4f6"
                        : isVerified
                          ? "#e8f0e2"
                          : "#fee2e2",
                      color: isInitial
                        ? "#000000"
                        : isVerified
                          ? "#1e8e3e"
                          : "#e71212",
                      borderRadius: "16px",
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: "none",
                        backgroundColor: isInitial
                          ? "#f3f4f6"
                          : isVerified
                            ? "#e8f0e2"
                            : "#fee2e2",
                      },
                      "&:disabled": {
                        backgroundColor: isVerified ? "#E7F5DB" : "#f3f4f6",
                        color: isVerified ? "#5F9B20" : "#000000",
                      },
                    }}
                  >
                    {isVerified ? "Verified" : "Verify Domain"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        {/* CNAME Record Display */}
        <Grid sx={{ mt: 2 }} container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography sx={styles.fieldLabel}>CNAME Record</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box sx={styles.fieldWrapper}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: { xs: "12px", sm: "16px" },
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "var(--medium-border-radius, 8px)",
                  gap: { xs: 1, sm: 2 },
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#6b7280",
                    fontFamily: "var(--font-family-manrope)",
                    fontWeight: 500,
                    minWidth: { xs: "50px", sm: "70px" },
                    whiteSpace: "nowrap",
                  }}
                >
                  CNAME
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontFamily: "monospace",
                    color: "#374151",
                    flex: 1,
                    backgroundColor: "#ffffff",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    msOverflowStyle: "none",
                    scrollbarWidth: "none",
                  }}
                >
                  dashboard.mapranking.com
                </Typography>
                <Tooltip
                  title={copied ? "Copied" : "Copy CNAME"}
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ffffff",
                        color: "#5e5d5dff",
                        border: "1px solid #d8d8d8",
                        fontSize: "12px",
                        borderRadius: "var(--small-border-radius)",
                        fontWeight: 500,
                        p: 1,
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#d8d8d8",
                      },
                    },
                  }}
                >
                  <IconButton
                    onClick={onCopy}
                    aria-label="copy cname"
                    size="small"
                    sx={{ flexShrink: 0 }}
                  >
                    {copied ? (
                      <CheckIcon size={18} />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ========================================================
            FORM ACTIONS
        ======================================================== */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "flex-end",
          mt: 4,
          pt: 3,
          borderTop: "1px solid #e5e7eb",
        }}
      >
        {!isedit && (
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              textTransform: "none",
              fontSize: "16px",
              fontFamily: "var(--font-family-manrope)",
              fontWeight: 500,
              padding: "10px 24px",
              borderRadius: "var(--action-button-radius)",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Reset
          </Button>
        )}
        <Button
          variant="contained"
          type="submit"
          color="primary"
          disableElevation
          sx={{
            width: "auto",
            backgroundColor: "var(--action-button-background-color, #1a73e8)",
            color: "#fff",
            border: "none",
            boxShadow: "none",
            cursor: "pointer",
            textTransform: "none",
            padding: "var(--action-button-small-padding)",
            borderRadius: "var(--action-button-radius)",
            fontFamily: "var(--font-family-manrope)",
            fontSize: "var(--buttons-font-size, 16px)",
            transition: "all 0.2s ease-in-out",
            fontWeight: "400 !important",
            "&:hover": {
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
              cursor: "not-allowed",
            },
          }}
        >
          {isedit ? "Save Changes" : "Create Workspace"}
        </Button>
      </Box>
    </form>
  );
};

export default AgencySettings;
